import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
//@ts-ignore
import * as styles from './product-variants.module.css';
//@ts-ignore
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import ArrowLgButton from '../Global/ArrowLgButton';
import {
    KontentMedia,
    KontentRichText,
    KontentStringValue,
} from '../../../types/KontentProps';
import RichText from '../Global/RichText';
import { getFluidImageData } from '../Global/DataUtils/gatsbyImageUtils';
import GatsbyImage from 'gatsby-image';

type Variant = {
    elements: {
        description: KontentRichText;
        headline: KontentStringValue;
        subheadline: KontentStringValue;
        primary_image: KontentMedia;
        secondary_image: KontentMedia;
    };
};

interface Props {
    headline: KontentStringValue;
    short_description: KontentRichText;
    variants: {
        value: Variant[];
    };
}

const ProductVariants: React.FC<Props> = ({
    headline,
    short_description,
    ...props
}) => {
    const breakpoints = useBreakpoint();
    const productVariants = props.variants.value;
    const currentDisplayNumber = breakpoints.md ? 4 : 1;
    const [visibleVariants, setVisibleVariants] = useState(
        productVariants.slice(0, currentDisplayNumber)
    );
    const [currentVariant, setCurrentVariant] = useState(0);
    const numVariants = productVariants.length;
    const strokeColor = '#505251';
    const showArrows = numVariants > currentDisplayNumber;

    useEffect(() => {
        setVisibleVariants(productVariants.slice(0, currentDisplayNumber));
    }, [breakpoints]);

    const handleClickNext = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        let newVariant = currentVariant + 1;

        let newVisibleVariants = productVariants.slice(
            newVariant,
            newVariant + currentDisplayNumber
        );

        setVisibleVariants(newVisibleVariants);
        setCurrentVariant(newVariant);
    };

    const handleClickPrev = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        let newVariant = currentVariant - 1;

        let newVisibleVariants = productVariants.slice(
            newVariant,
            newVariant + currentDisplayNumber
        );

        setCurrentVariant(newVariant);
        setVisibleVariants(newVisibleVariants);
    };

    const renderVariant = (variant: Variant, idx: number) => {
        if (breakpoints.md || idx === 0) {
            const topImage = getFluidImageData(variant.elements.primary_image);
            const bottomImage = getFluidImageData(
                variant.elements.secondary_image
            );

            return (
                <div className={styles.variant}>
                    {topImage.source && (
                        <GatsbyImage
                            fluid={topImage.source}
                            alt={topImage.alt}
                            className={styles.topImageContainer}
                        />
                    )}
                    <div className={styles.borderContainer}>
                        {bottomImage.source && (
                            <GatsbyImage
                                fluid={bottomImage.source}
                                alt={bottomImage.alt}
                                className={styles.bottomImageContainer}
                            />
                        )}
                        <h3 className={styles.variantTitle}>
                            {variant.elements.headline.value}
                        </h3>
                        <p className={styles.variantSubheading}>
                            {variant.elements.subheadline.value}
                        </p>
                        <div className={styles.variantDescription}>
                            {RichText(variant.elements.description)}
                        </div>
                    </div>
                </div>
            );
        }
    };

    return (
        <section className={styles.wrapper}>
            <h2 className={styles.title}>{headline.value}</h2>
            <div className={styles.description}>
                <p>{RichText(short_description)}</p>
            </div>
            <div
                className={styles.carouselContainer}
                style={
                    visibleVariants.length <= 3 && breakpoints.md
                        ? { display: 'flex', justifyContent: 'center' }
                        : {}
                }
            >
                <div
                    className={styles.variantsContainer}
                    style={
                        visibleVariants.length <= 3 && breakpoints.md
                            ? {
                                  gridTemplateColumns: `repeat(${
                                      visibleVariants.length
                                  }, minmax(${
                                      visibleVariants.length == 3
                                          ? '220px'
                                          : '200px'
                                  }, 400px))`,
                                  textAlign: 'center',
                              }
                            : {}
                    }
                >
                    {visibleVariants.map((variant, idx) =>
                        renderVariant(variant, idx)
                    )}
                </div>
                {showArrows && currentVariant != 0 ? (
                    <ArrowLgButton
                        direction="left"
                        handleClick={handleClickPrev}
                        strokeColor={strokeColor}
                        className={styles.prev}
                    />
                ) : null}
                {showArrows &&
                currentVariant !=
                    productVariants.length - currentDisplayNumber ? (
                    <ArrowLgButton
                        direction="right"
                        handleClick={handleClickNext}
                        strokeColor={strokeColor}
                        className={styles.next}
                    />
                ) : null}
            </div>
        </section>
    );
};

export default ProductVariants;

export const fragmentProductVariants = graphql`
    fragment ProductVariants on kontent_item_component___product_variants {
        id
        elements {
            headline {
                value
            }
            short_description {
                ...kontentRichText
            }
            variants {
                value {
                    ... on kontent_item_content_item___variant {
                        id
                        elements {
                            description {
                                ...kontentRichText
                            }
                            headline {
                                value
                            }
                            primary_image {
                                ...kontentImage
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    fluid(
                                                        maxWidth: 327
                                                        maxHeight: 273
                                                        quality: 100
                                                    ) {
                                                        ...KontentAssetFluid
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            secondary_image {
                                ...kontentImage
                                value {
                                    ... on kontent_item_media___image {
                                        id
                                        elements {
                                            file {
                                                value {
                                                    fluid(
                                                        maxWidth: 250
                                                        maxHeight: 262
                                                        quality: 100
                                                    ) {
                                                        ...KontentAssetFluid
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                            subheadline {
                                value
                            }
                        }
                    }
                }
            }
        }
    }
`;
